import { useMemo, useState } from 'react';
import { Globe, Home, Pause, Play, Search, Server } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@easyflow/ui-frontend/components/ui/accordion';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Checkbox } from '@easyflow/ui-frontend/components/ui/checkbox';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { paOperationSemanticsSchema, type PaOperationSemantics, type PaToolPermission } from '@easyflow/shared';

export interface AgentEditorTool {
  id: string;
  tool_name: string;
  description: string;
  mcp_server_id: string;
  server_name?: string;
  is_internal?: boolean;
  category?: string | null;
  tags?: string[];
  short_description?: string | null;
  enabled?: boolean;
  operationMode?: PaOperationSemantics['kind'] | 'unknown';
  supportsBulkOperations?: boolean;
  requiresBulkPermission?: boolean;
}

export function withPaOperationMode<T extends AgentEditorTool & { operation_semantics?: unknown }>(tool: T): T & {
  operationMode: PaOperationSemantics['kind'] | 'unknown';
  supportsBulkOperations: boolean;
  requiresBulkPermission: boolean;
} {
  const semantics = paOperationSemanticsSchema.safeParse(tool.operation_semantics);
  if (!semantics.success) return { ...tool, operationMode: 'unknown', supportsBulkOperations: true, requiresBulkPermission: true };
  const operations = semantics.data.kind === 'actions' ? Object.values(semantics.data.operations) : [semantics.data];
  return {
    ...tool,
    operationMode: semantics.data.kind,
    supportsBulkOperations: operations.some(operation => operation.kind !== 'read'),
    requiresBulkPermission: operations.length > 0 && operations.every(operation => operation.kind === 'bulk' || operation.kind === 'indirect'),
  };
}

const requiresBulkPermission = (tool: AgentEditorTool) =>
  tool.requiresBulkPermission ?? (tool.operationMode === 'bulk' || tool.operationMode === 'indirect' || tool.operationMode === 'unknown');

const DOMAIN_LABELS: Record<string, { label: string; order: number }> = {
  telephony: { label: 'Telefonia', order: 1 },
  crm: { label: 'CRM', order: 2 },
  hospitality: { label: 'Hospitality', order: 3 },
  workflow: { label: 'Workflow', order: 4 },
  workflow_control: { label: 'Controllo Flusso', order: 5 },
  calendar: { label: 'Calendario', order: 6 },
  messaging: { label: 'Messaggistica', order: 7 },
  telegram: { label: 'Telegram', order: 8 },
  campaign: { label: 'Campagne', order: 9 },
  ai: { label: 'AI', order: 10 },
  knowledge: { label: 'Knowledge Base', order: 11 },
  web: { label: 'Web & Ricerca', order: 12 },
  supplychain: { label: 'Supply Chain & Catalogo', order: 13 },
};

export const toolIdentity = (tool: Pick<AgentEditorTool, 'mcp_server_id' | 'tool_name'>) =>
  JSON.stringify([tool.mcp_server_id, tool.tool_name]);

export function applyToolPermissionChanges(current: PaToolPermission[], changes: PaToolPermission[]): PaToolPermission[] {
  const next = new Map(current.map(permission => [JSON.stringify([permission.server_id, permission.tool_name]), permission]));
  changes.forEach(permission => next.set(JSON.stringify([permission.server_id, permission.tool_name]), permission));
  return [...next.values()];
}

export function genericToolChanges(tools: AgentEditorTool[], permissions: PaToolPermission[], enabled: boolean): PaToolPermission[] {
  return tools.filter(tool => !enabled || (tool.enabled !== false && !requiresBulkPermission(tool))).map(tool => {
    const previous = permissions.find(permission => permission.server_id === tool.mcp_server_id && permission.tool_name === tool.tool_name);
    return { server_id: tool.mcp_server_id, tool_name: tool.tool_name, enabled, bulk_allowed: previous ? previous.bulk_allowed : false };
  });
}

interface Props {
  tools: AgentEditorTool[];
  permissions: PaToolPermission[];
  onChange: (changes: PaToolPermission[]) => void;
  disabled?: boolean;
  maxTools?: number;
  searchQuery?: string;
}

export function AgentToolPermissionsEditor({ tools, permissions, onChange, disabled = false, maxTools = 128, searchQuery }: Props) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const permissionMap = useMemo(() => new Map(permissions.map(permission => [
    JSON.stringify([permission.server_id, permission.tool_name]), permission,
  ])), [permissions]);
  const groups = useMemo(() => {
    const result = new Map<string, { label: string; internal: boolean; order: number; tools: AgentEditorTool[] }>();
    for (const tool of tools) {
      const domain = tool.category || 'altri';
      const key = tool.is_internal ? `internal:${domain}` : `server:${tool.mcp_server_id}`;
      const group = result.get(key) ?? {
        label: tool.is_internal ? DOMAIN_LABELS[domain]?.label || domain : tool.server_name || tool.mcp_server_id,
        internal: tool.is_internal === true,
        order: tool.is_internal ? DOMAIN_LABELS[domain]?.order ?? 99 : 100,
        tools: [],
      };
      group.tools.push(tool);
      result.set(key, group);
    }
    return [...result.entries()].sort(([, a], [, b]) => a.order - b.order);
  }, [tools]);
  const query = (searchQuery ?? search).trim().toLocaleLowerCase();
  const matches = (tool: AgentEditorTool) => [tool.tool_name, tool.description, tool.short_description, tool.server_name, ...(tool.tags ?? [])]
    .some(value => value?.toLocaleLowerCase().includes(query));
  const visibleTools = tools.filter(matches);
  const selectedTools = visibleTools.filter(tool => selected.has(toolIdentity(tool)));
  const enabledCount = tools.filter(tool => permissionMap.get(toolIdentity(tool))?.enabled).length;
  const batch = (items: AgentEditorTool[], enabled: boolean) => onChange(genericToolChanges(items, permissions, enabled));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        {searchQuery === undefined && <div className="relative flex-1 min-w-40">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input aria-label="Cerca strumenti" placeholder="Cerca tools..." value={search} onChange={event => setSearch(event.target.value)} className="pl-9" />
        </div>}
        <Badge variant={enabledCount > maxTools ? 'destructive' : 'outline'}>{enabledCount}/{maxTools} abilitati</Badge>
      </div>
      {enabledCount > maxTools && <p role="alert" className="text-sm text-destructive">Troppi strumenti abilitati: massimo {maxTools}.</p>}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="ghost" disabled={disabled || visibleTools.length === 0} onClick={() => setSelected(new Set(visibleTools.map(toolIdentity)))}>Seleziona visibili ({visibleTools.length})</Button>
        {selected.size > 0 && <>
          <span className="text-xs text-muted-foreground">{selectedTools.length} selezionati visibili</span>
          <Button type="button" size="sm" variant="outline" disabled={disabled || !selectedTools.length} onClick={() => batch(selectedTools, true)}>Abilita</Button>
          <Button type="button" size="sm" variant="outline" disabled={disabled || !selectedTools.length} onClick={() => batch(selectedTools, false)}>Disabilita</Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Deseleziona</Button>
        </>}
      </div>
      <Accordion type="multiple" defaultValue={['internal', 'external']} className="space-y-2">
        {[true, false].map(internal => {
          const categoryTools = tools.filter(tool => Boolean(tool.is_internal) === internal);
          return (
            <AccordionItem key={String(internal)} value={internal ? 'internal' : 'external'} className="border rounded-lg">
              <AccordionTrigger className="px-3 py-2 hover:no-underline">
                <span className="flex items-center gap-2">
                  {internal ? <Home className="h-4 w-4 text-accent" /> : <Globe className="h-4 w-4 text-info" />}
                  {internal ? 'Tools Interni' : 'Tools Esterni'}
                  <Badge variant="secondary">{categoryTools.filter(tool => permissionMap.get(toolIdentity(tool))?.enabled).length}/{categoryTools.length}</Badge>
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-3 pb-3">
                {groups.filter(([, group]) => group.internal === internal).map(([key, group]) => {
                  const visible = group.tools.filter(matches);
                  if (!visible.length) return null;
                  return <div key={key} className="mb-3 space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        {!internal && <Server className="h-3 w-3" />}{group.label}
                        <span>{group.tools.filter(tool => permissionMap.get(toolIdentity(tool))?.enabled).length}/{group.tools.length}</span>
                      </span>
                      <div className="flex gap-1">
                        <Button type="button" size="sm" variant="ghost" className="h-6 text-xs" disabled={disabled} onClick={() => batch(visible, true)}><Play className="h-3 w-3 mr-1" />Tutti{query ? ' visibili' : ''}</Button>
                        <Button type="button" size="sm" variant="ghost" className="h-6 text-xs" disabled={disabled} onClick={() => batch(visible, false)}><Pause className="h-3 w-3 mr-1" />Nessuno{query ? ' visibile' : ''}</Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                      {visible.map(tool => {
                        const identity = toolIdentity(tool);
                        const permission = permissionMap.get(identity);
                        const enabled = permission?.enabled ?? false;
                        const bulkTool = requiresBulkPermission(tool);
                        const showBulk = tool.supportsBulkOperations ?? (bulkTool || tool.operationMode === 'single' || tool.operationMode === 'variable');
                        return <div key={identity} className={`p-2 rounded-lg border space-y-2 ${enabled ? 'bg-primary/10 border-primary' : 'bg-muted/30'}`}>
                          <div className="flex items-start gap-2">
                            <Checkbox aria-label={`Seleziona ${tool.server_name || tool.mcp_server_id}: ${tool.tool_name}`} checked={selected.has(identity)} disabled={disabled} onCheckedChange={checked => setSelected(previous => {
                              const next = new Set(previous);
                              if (checked === true) next.add(identity); else next.delete(identity);
                              return next;
                            })} />
                            <label className="flex items-start gap-2 flex-1 min-w-0 text-xs">
                              <Checkbox aria-label={`Abilita ${tool.server_name || tool.mcp_server_id}: ${tool.tool_name}`} checked={enabled} disabled={disabled || (!enabled && tool.enabled === false)} onCheckedChange={checked => onChange([{
                                server_id: tool.mcp_server_id, tool_name: tool.tool_name, enabled: checked === true,
                                bulk_allowed: bulkTool && checked === true ? true : permission ? permission.bulk_allowed : false,
                              }])} />
                              <span className="break-all font-medium">{tool.tool_name}{bulkTool ? ' — operazioni massive' : ''}</span>
                            </label>
                          </div>
                          <p className="text-xs text-muted-foreground break-words">{tool.short_description || tool.description}</p>
                          {tool.enabled === false && <p className="text-xs text-muted-foreground">Server o strumento disabilitato nel catalogo.</p>}
                          {(tool.operationMode === 'indirect' || tool.operationMode === 'unknown') && <p className="text-xs text-warning">Operazione non delimitabile: non disponibile come operazione singola.</p>}
                          {showBulk && <label className="flex items-center gap-2 text-xs">
                            <Checkbox aria-label={`Operazioni massive ${tool.server_name || tool.mcp_server_id}: ${tool.tool_name}`} disabled={disabled || !enabled} checked={enabled && permission?.bulk_allowed !== false} onCheckedChange={checked => onChange([{
                              server_id: tool.mcp_server_id, tool_name: tool.tool_name,
                              enabled: bulkTool && checked !== true ? false : enabled, bulk_allowed: checked === true,
                            }])} />Consenti operazioni massive
                          </label>}
                          {showBulk && enabled && permission?.bulk_allowed === null && <p className="text-xs text-warning">Compatibilità precedente: capacità massiva non ancora limitata esplicitamente.</p>}
                        </div>;
                      })}
                    </div>
                  </div>;
                })}
                {!categoryTools.some(matches) && <p className="text-xs text-muted-foreground py-2">Nessuno strumento disponibile per questa ricerca.</p>}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}